import {
  AuthenticationDetails, CognitoUser, CognitoUserAttribute, CognitoUserPool, type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { config } from '../config';

/**
 * Cognito 로그인(SRP — 비밀번호를 서버로 보내지 않는 방식).
 * 토큰은 sessionStorage 에 둔다: 탭·브라우저를 닫으면 로그아웃(공용 PC 대비).
 * API 에는 ID 토큰을 보낸다 — 역할(custom:role)·센터(custom:centerId) 클레임이 ID 토큰에만 있다.
 */
let pool: CognitoUserPool | null = null;
const getPool = () => (pool ??= new CognitoUserPool({ UserPoolId: config.userPoolId, ClientId: config.clientId, Storage: window.sessionStorage }));

export type SignInResult =
  | { kind: 'ok' }
  | { kind: 'newPassword'; complete: (newPassword: string) => Promise<SignInResult> }
  | { kind: 'totp'; complete: (code: string) => Promise<SignInResult> };

const KNOWN_ERRORS: Record<string, string> = {
  NotAuthorizedException: '이메일 또는 비밀번호가 맞지 않습니다.',
  UserNotFoundException: '이메일 또는 비밀번호가 맞지 않습니다.',
  PasswordResetRequiredException: '비밀번호 재설정이 필요합니다. 관리자에게 문의하세요.',
  InvalidPasswordException: '비밀번호는 10자 이상이며 영문 소문자와 숫자를 포함해야 합니다.',
  CodeMismatchException: '인증 코드가 맞지 않습니다.',
  ExpiredCodeException: '인증 코드가 만료되었습니다. 코드를 다시 받아 주세요.',
  UsernameExistsException: '이미 가입된 이메일입니다. 로그인하거나 비밀번호 찾기를 이용하세요.',
  UserNotConfirmedException: '이메일 인증이 끝나지 않은 계정입니다. 회원가입에서 같은 이메일로 인증 코드를 다시 받아 주세요.',
  InvalidParameterException: '입력한 정보를 다시 확인해 주세요.',
  TooManyRequestsException: '시도가 너무 많습니다. 잠시 후 다시 시도하세요.',
  LimitExceededException: '시도가 너무 많습니다. 잠시 후 다시 시도하세요.',
};
const friendly = (e: unknown) => {
  const err = e as { code?: string; name?: string; message?: string };
  const code = err.code ?? err.name ?? '';
  // 가입 전 트리거가 막은 이유(역할·나이)는 트리거 메시지를 그대로 보여 준다
  if (code === 'UserLambdaValidationException' && err.message) return new Error(err.message.replace(/^PreSignUp failed with error /, '').replace(/\.+$/, '') + '.');
  return new Error(KNOWN_ERRORS[code] ?? '처리하지 못했습니다. 잠시 후 다시 시도하세요.');
};

/**
 * 직접 가입 1단계: 계정 생성 + 이메일 인증 코드 발송.
 * 역할은 custom:signupRole 로 보내고, 서버 트리거가 검사한 뒤 인증이 끝나면 custom:role 로 옮긴다.
 * 학생의 생년월일은 가입 전 나이 검사용(validationData)으로만 보낸다.
 */
export function signUp(i: { email: string; password: string; name: string; role: string; birthDate?: string }): Promise<void> {
  const attrs = [
    new CognitoUserAttribute({ Name: 'name', Value: i.name.trim() }),
    new CognitoUserAttribute({ Name: 'custom:signupRole', Value: i.role }),
  ];
  const validation = i.birthDate ? [new CognitoUserAttribute({ Name: 'birthDate', Value: i.birthDate })] : [];
  return new Promise((resolve, reject) => getPool().signUp(i.email.trim().toLowerCase(), i.password, attrs, validation, (e) => {
    if (!e) return resolve();
    // 가입만 하고 인증을 끝내지 않은 이메일이면 코드를 다시 보내고 인증 단계로 이어 간다(이미 인증된 계정이면 그대로 '이미 가입됨')
    if ((e as { code?: string }).code === 'UsernameExistsException') resendSignUpCode(i.email).then(resolve, () => reject(friendly(e)));
    else reject(friendly(e));
  }));
}

/** 직접 가입 2단계: 메일로 받은 6자리 코드 확인 */
export function confirmSignUp(email: string, code: string): Promise<void> {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: getPool(), Storage: window.sessionStorage });
  return new Promise((resolve, reject) => user.confirmRegistration(code.trim(), true, (e) => (e ? reject(friendly(e)) : resolve())));
}

export function resendSignUpCode(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: getPool(), Storage: window.sessionStorage });
  return new Promise((resolve, reject) => user.resendConfirmationCode((e) => (e ? reject(friendly(e)) : resolve())));
}

export function signIn(email: string, password: string): Promise<SignInResult> {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: getPool(), Storage: window.sessionStorage });
  const details = new AuthenticationDetails({ Username: email.trim().toLowerCase(), Password: password });
  return new Promise((resolve, reject) => {
    const callbacks = {
      onSuccess: () => resolve({ kind: 'ok' as const }),
      onFailure: (e: unknown) => reject(friendly(e)),
      newPasswordRequired: () => resolve({
        kind: 'newPassword',
        complete: (newPassword: string) => new Promise<SignInResult>((res, rej) => user.completeNewPasswordChallenge(newPassword, {}, {
          onSuccess: () => res({ kind: 'ok' }), onFailure: (e) => rej(friendly(e)),
        })),
      }),
      totpRequired: () => resolve({
        kind: 'totp',
        complete: (code: string) => new Promise<SignInResult>((res, rej) => user.sendMFACode(code, {
          onSuccess: () => res({ kind: 'ok' }), onFailure: (e) => rej(friendly(e)),
        }, 'SOFTWARE_TOKEN_MFA')),
      }),
    };
    user.authenticateUser(details, callbacks);
  });
}

/** 유효한 세션의 ID 토큰(만료 시 refresh 토큰으로 자동 갱신). 없으면 null. */
export function idToken(): Promise<string | null> {
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => user.getSession((err: Error | null, session: CognitoUserSession | null) => {
    resolve(!err && session?.isValid() ? session.getIdToken().getJwtToken() : null);
  }));
}

/** 비밀번호 재설정 1단계: 가입 이메일로 인증 코드 발송(없는 이메일이어도 같은 응답 — 계정 존재 여부를 숨긴다). */
export function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: getPool(), Storage: window.sessionStorage });
  return new Promise((resolve, reject) => user.forgotPassword({
    onSuccess: () => resolve(), inputVerificationCode: () => resolve(),
    onFailure: (e) => ((e as { code?: string }).code === 'UserNotFoundException' ? resolve() : reject(friendly(e))),
  }));
}

/** 비밀번호 재설정 2단계: 메일로 받은 코드 + 새 비밀번호 */
export function confirmPassword(email: string, code: string, newPassword: string): Promise<void> {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: getPool(), Storage: window.sessionStorage });
  return new Promise((resolve, reject) => user.confirmPassword(code.trim(), newPassword, { onSuccess: () => resolve(), onFailure: (e) => reject(friendly(e)) }));
}

/** 로그인한 본인 계정을 지운다. 사용자 토큰으로 하므로 서버 권한이 필요 없다. */
export function deleteCurrentUser(): Promise<void> {
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve();
  return new Promise((resolve, reject) => user.getSession((err: Error | null) => {
    if (err) { reject(err); return; }
    user.deleteUser((e) => { if (e) reject(e); else resolve(); });
  }));
}

export function signOut() {
  getPool().getCurrentUser()?.signOut();
}
